import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PlayIcon, PauseIcon, DownloadIcon } from 'lucide-react';

interface VideoPlayerProps {
  videoData?: string;
  videoDataUrl?: string | null;
  download?: boolean;
  audioBar?: boolean;
  playButton?: boolean;
  disabled?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoDataUrl,
  download = false,
  playButton = false,
  disabled = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(videoDataUrl || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!videoDataUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const objectUrl: string | null = null;

    const processVideo = async () => {
      try {
        // If we have a direct URL, use it
        if (videoDataUrl) {
          setVideoUrl(videoDataUrl);
          setIsLoading(false);
          return;
        }
      } catch (e) {
        console.error('Error processing video data:', e);
        setError(`Failed to load video: ${e}`);
      } finally {
        setIsLoading(false);
      }
    };

    processVideo();

    return () => {
      // On cleanup, revoke the object URL to prevent memory leaks.
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [videoDataUrl]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleDownload = () => {
    if (videoUrl) {
      const a = document.createElement('a');
      a.href = videoUrl;
      a.download = 'video.mp4';
      a.click();
    }
  };

  if (isLoading) {
    return <div>Loading video...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!videoUrl) {
    return null;
  }

  return (
    <div className='flex flex-col items-center gap-4'>
      {/* Main video player */}
      <video
        ref={videoRef}
        src={videoUrl}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        preload='auto'
        controls={!playButton}
        style={{
          display: playButton ? 'none' : 'block',
        }}
      />

      {/* Play button controls */}
      {playButton && (
        <div className='flex items-center gap-2'>
          <Button
            size='icon'
            variant='ghost'
            onClick={togglePlayPause}
            disabled={disabled}
          >
            {isPlaying ? (
              <PauseIcon className='w-4 h-4' />
            ) : (
              <PlayIcon className='w-4 h-4' />
            )}
          </Button>

          {/* Hidden video element for play button mode */}
          <video
            ref={videoRef}
            src={videoUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            preload='auto'
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Download button */}
      {download && (
        <Button
          size='sm'
          variant='outline'
          className='flex items-center gap-2'
          onClick={handleDownload}
        >
          <DownloadIcon className='w-4 h-4' />
          Download Video
        </Button>
      )}
    </div>
  );
};

export default VideoPlayer;
